/**
 * Raffinato Theme JavaScript
 * Luxury jewelry e-commerce functionality
 */

class RaffinatoTheme {
  constructor() {
    this.init();
  }

  init() {
    this.initMobileMenu();
    this.initStickyHeader();
    this.initProductGallery();
    this.initAccordions();
    this.initQuantitySelectors();
    this.initVariantSelector();
    this.initCartDrawer();
    this.initSearchModal();
    this.initQuickView();
    this.initFilters();
    this.initColorSwatches();
    this.initLanguageToggle();
  }

  // Mobile Menu
  initMobileMenu() {
    const toggleBtn = document.querySelector('.mobile-menu-toggle');
    const mobileMenu = document.querySelector('.mobile-menu');

    if (!toggleBtn || !mobileMenu) return;

    toggleBtn.addEventListener('click', () => {
      mobileMenu.classList.toggle('mobile-menu--active');
      document.body.classList.toggle('menu-open');
    });

    // Close menu on outside click
    document.addEventListener('click', (e) => {
      if (!toggleBtn.contains(e.target) && !mobileMenu.contains(e.target)) {
        mobileMenu.classList.remove('mobile-menu--active');
        document.body.classList.remove('menu-open');
      }
    });
  }

  // Sticky Header
  initStickyHeader() {
    const header = document.querySelector('.header--sticky');
    if (!header) return;

    let lastScroll = 0;
    const headerHeight = header.offsetHeight;

    window.addEventListener('scroll', () => {
      const currentScroll = window.pageYOffset;

      if (currentScroll > headerHeight) {
        if (currentScroll > lastScroll) {
          header.style.transform = 'translateY(-100%)';
        } else {
          header.style.transform = 'translateY(0)';
        }
      }

      lastScroll = currentScroll;
    });
  }

  // Product Gallery
  initProductGallery() {
    const thumbs = document.querySelectorAll('.product-gallery__thumb');
    const slides = document.querySelectorAll('.product-gallery__slide');

    if (!thumbs.length || !slides.length) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const mediaId = thumb.dataset.mediaId;

        // Update active states
        thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');

        slides.forEach(slide => {
          if (slide.dataset.mediaId === mediaId) {
            slide.classList.add('active');
          } else {
            slide.classList.remove('active');
          }
        });
      });
    });
  }

  // Accordions
  initAccordions() {
    const triggers = document.querySelectorAll('.accordion__trigger');

    triggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const contentId = trigger.dataset.accordion;
        const content = document.getElementById(contentId);

        if (!content) return;

        trigger.classList.toggle('active');
        content.classList.toggle('active');

        // Close other accordions
        triggers.forEach(otherTrigger => {
          if (otherTrigger !== trigger) {
            otherTrigger.classList.remove('active');
            const otherContent = document.getElementById(otherTrigger.dataset.accordion);
            if (otherContent) otherContent.classList.remove('active');
          }
        });
      });
    });
  }

  // Quantity Selectors
  initQuantitySelectors() {
    const quantityButtons = document.querySelectorAll('.quantity__button');

    quantityButtons.forEach(button => {
      button.addEventListener('click', () => {
        const input = button.parentElement.querySelector('.quantity__input');
        const currentValue = parseInt(input.value) || 1;
        const action = button.dataset.action;

        if (action === 'plus') {
          input.value = currentValue + 1;
        } else if (action === 'minus' && currentValue > 1) {
          input.value = currentValue - 1;
        }

        // Trigger change event
        input.dispatchEvent(new Event('change'));
      });
    });
  }

  // Variant Selector
  initVariantSelector() {
    const optionButtons = document.querySelectorAll('.product-option__value');

    optionButtons.forEach(button => {
      button.addEventListener('click', () => {
        const optionIndex = button.dataset.optionIndex;
        const value = button.dataset.value;

        // Update selected state
        const siblings = button.parentElement.querySelectorAll('.product-option__value');
        siblings.forEach(sibling => sibling.classList.remove('selected'));
        button.classList.add('selected');

        // Update variant (would need actual variant matching logic)
        this.updateSelectedVariant();
      });
    });
  }

  updateSelectedVariant() {
    // This would contain logic to:
    // 1. Find matching variant based on selected options
    // 2. Update price display
    // 3. Update availability
    // 4. Update hidden variant ID input
    // For now, this is a placeholder
    console.log('Variant updated');
  }

  // Cart Drawer
  initCartDrawer() {
    const cartTriggers = document.querySelectorAll('[data-cart-trigger]');
    const cartDrawer = document.querySelector('.cart-drawer');
    const cartClose = document.querySelector('.cart-drawer__close');

    if (!cartDrawer) return;

    cartTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.openCartDrawer();
      });
    });

    if (cartClose) {
      cartClose.addEventListener('click', () => {
        this.closeCartDrawer();
      });
    }

    // Add to cart form submission
    const addToCartForms = document.querySelectorAll('form[action="/cart/add"]');
    addToCartForms.forEach(form => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart(form);
      });
    });
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('.cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.add('cart-drawer--open');
      document.body.classList.add('drawer-open');
    }
  }

  closeCartDrawer() {
    const cartDrawer = document.querySelector('.cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.remove('cart-drawer--open');
      document.body.classList.remove('drawer-open');
    }
  }

  async addToCart(form) {
    const formData = new FormData(form);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const item = await response.json();
        this.updateCartDrawer();
        this.openCartDrawer();
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  }

  async updateCartDrawer() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Update cart count
      const cartCounts = document.querySelectorAll('.header__cart-count');
      cartCounts.forEach(count => {
        count.textContent = cart.item_count;
        if (cart.item_count > 0) {
          count.style.display = 'flex';
        } else {
          count.style.display = 'none';
        }
      });

      // Update cart drawer content
      // This would render the cart items in the drawer
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  // Search Modal
  initSearchModal() {
    const searchTriggers = document.querySelectorAll('[data-search-trigger]');
    const searchModal = document.querySelector('.search-modal');
    const searchClose = document.querySelector('.search-modal__close');

    if (!searchModal) return;

    searchTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        searchModal.classList.add('search-modal--open');
        document.body.classList.add('modal-open');

        // Focus search input
        const searchInput = searchModal.querySelector('.search-modal__input');
        if (searchInput) {
          setTimeout(() => searchInput.focus(), 100);
        }
      });
    });

    if (searchClose) {
      searchClose.addEventListener('click', () => {
        searchModal.classList.remove('search-modal--open');
        document.body.classList.remove('modal-open');
      });
    }

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && searchModal.classList.contains('search-modal--open')) {
        searchModal.classList.remove('search-modal--open');
        document.body.classList.remove('modal-open');
      }
    });
  }

  // Quick View
  initQuickView() {
    const quickViewButtons = document.querySelectorAll('.product-card__quick-view');

    quickViewButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        const handle = button.dataset.productHandle;

        // Get image from product card as fallback
        const productCard = button.closest('.product-card');
        const cardImage = productCard ? productCard.querySelector('.product-card__image--primary') : null;
        const fallbackImageUrl = cardImage ? cardImage.src : null;

        await this.loadQuickView(handle, fallbackImageUrl);
      });
    });
  }

  async loadQuickView(handle, fallbackImageUrl) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      const product = await response.json();

      console.log('Quick View - Full product data:', product);

      // Render quick view modal with product data
      this.renderQuickViewModal(product, fallbackImageUrl);
    } catch (error) {
      console.error('Error loading quick view:', error);
    }
  }

  renderQuickViewModal(product, fallbackImageUrl) {
    const modal = document.querySelector('.quick-view-modal');
    if (!modal) return;

    const content = document.getElementById('quickViewContent');
    if (!content) return;

    // Build the quick view HTML
    const firstVariant = product.variants[0];
    const isAvailable = product.available;

    // Get image URL - Shopify's .js endpoint returns featured_image as string URL
    let imageUrl = product.featured_image || '';

    // Fallback to first image in images array
    if (!imageUrl && product.images && product.images.length > 0) {
      imageUrl = product.images[0];
    }

    // Fallback to variant image
    if (!imageUrl && firstVariant && firstVariant.featured_image) {
      imageUrl = firstVariant.featured_image;
    }

    // Use fallback from product card if still no image
    if (!imageUrl && fallbackImageUrl) {
      imageUrl = fallbackImageUrl;
    }

    console.log('Quick View - Product:', product.title, 'Image URL:', imageUrl, 'Fallback:', fallbackImageUrl);

    // Build price or sold out display
    const priceDisplay = isAvailable ? `
      <div class="quick-view__price">
        <span>${(firstVariant.price / 100).toFixed(2)}</span>
        <span class="currency-symbol">
          <svg class="icon-sar" width="14" height="14" viewBox="0 0 1124.14 1256.39" fill="currentColor">
            <path d="M699.62,1113.02h0c-20.06,44.48-33.32,92.75-38.4,143.37l424.51-90.24c20.06-44.47,33.31-92.75,38.4-143.37l-424.51,90.24Z"/>
            <path d="M1085.73,895.8c20.06-44.47,33.32-92.75,38.4-143.37l-330.68,70.33v-135.2l292.27-62.11c20.06-44.47,33.32-92.75,38.4-143.37l-330.68,70.27V66.13c-50.67,28.45-95.67,66.32-132.25,110.99v403.35l-132.25,28.11V0c-50.67,28.44-95.67,66.32-132.25,110.99v525.69l-295.91,62.88c-20.06,44.47-33.33,92.75-38.42,143.37l334.33-71.05v170.26l-358.3,76.14c-20.06,44.47-33.32,92.75-38.4,143.37l375.04-79.7c30.53-6.35,56.77-24.4,73.83-49.24l68.78-101.97v-.02c7.14-10.59,11.46-23.15,11.46-36.71v-98.84l132.25-28.12v135.2c0,13.55,4.31,26.12,11.46,36.71v.02l68.78,101.97c17.06,24.84,43.3,42.89,73.83,49.24l9.66-2.05h0c50.67-28.44,95.67-66.32,132.25-110.99v-98.46l292.28-62.11Z"/>
          </svg>
        </span>
      </div>
    ` : `<div class="quick-view__sold-out">Sold Out</div>`;

    const html = `
      <div class="quick-view-grid">
        <div class="quick-view__images">
          ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" class="quick-view__main-image" onerror="this.parentElement.innerHTML='<div class=\\'quick-view__placeholder\\'>Image not available</div>'">` : '<div class="quick-view__placeholder">No Image</div>'}
        </div>
        <div class="quick-view__details">
          <h2 class="quick-view__title">${product.title}</h2>
          ${priceDisplay}
          ${product.description ? `<div class="quick-view__description">${product.description}</div>` : ''}
          <a href="/products/${product.handle}" class="quick-view__button">View Full Details</a>
        </div>
      </div>
    `;

    content.innerHTML = html;
    modal.classList.add('quick-view-modal--open');
    document.body.classList.add('modal-open');

    // Add close handler
    const closeBtn = modal.querySelector('.quick-view-modal__close');
    const overlay = modal.querySelector('.quick-view-modal__overlay');

    const closeModal = () => {
      modal.classList.remove('quick-view-modal--open');
      document.body.classList.remove('modal-open');
    };

    if (closeBtn) closeBtn.onclick = closeModal;
    if (overlay) overlay.onclick = closeModal;
  }

  // Filters
  initFilters() {
    const filterDropdowns = document.querySelectorAll('.filter-dropdown');

    filterDropdowns.forEach(dropdown => {
      const trigger = dropdown.querySelector('.filter-dropdown__trigger');

      if (trigger) {
        trigger.addEventListener('click', (e) => {
          e.stopPropagation();

          // Close other dropdowns
          filterDropdowns.forEach(other => {
            if (other !== dropdown) {
              other.classList.remove('filter-dropdown--active');
            }
          });

          dropdown.classList.toggle('filter-dropdown--active');
        });
      }
    });

    // Close dropdowns on outside click
    document.addEventListener('click', () => {
      filterDropdowns.forEach(dropdown => {
        dropdown.classList.remove('filter-dropdown--active');
      });
    });

    // Handle filter checkbox changes
    const filterCheckboxes = document.querySelectorAll('.filter-option input[type="checkbox"]');
    filterCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        this.updateFilters();
      });
    });

    // Handle sort change
    const sortSelect = document.getElementById('sortBy');
    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        this.updateSort(sortSelect.value);
      });
    }

    // Clear all filters
    const clearButton = document.querySelector('.clear-all-filters');
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        this.clearAllFilters();
      });
    }
  }

  updateFilters() {
    // Collect all active filters
    const activeFilters = [];
    const checkboxes = document.querySelectorAll('.filter-option input[type="checkbox"]:checked');

    checkboxes.forEach(checkbox => {
      activeFilters.push({
        type: checkbox.dataset.filterType,
        value: checkbox.value
      });
    });

    // Update UI
    this.renderActiveFilters(activeFilters);

    // Update products (would make AJAX request to filter products)
    this.filterProducts(activeFilters);
  }

  renderActiveFilters(filters) {
    const container = document.getElementById('activeFilters');
    const list = container.querySelector('.active-filters__list');

    if (!filters.length) {
      container.style.display = 'none';
      return;
    }

    container.style.display = 'flex';
    list.innerHTML = filters.map(filter => `
      <span class="active-filter-tag" data-filter-type="${filter.type}" data-filter-value="${filter.value}">
        ${filter.value}
        <span class="active-filter-tag__remove">×</span>
      </span>
    `).join('');

    // Add remove handlers
    list.querySelectorAll('.active-filter-tag__remove').forEach(button => {
      button.addEventListener('click', (e) => {
        const tag = e.target.closest('.active-filter-tag');
        const type = tag.dataset.filterType;
        const value = tag.dataset.filterValue;

        // Uncheck corresponding checkbox
        const checkbox = document.querySelector(`input[data-filter-type="${type}"][value="${value}"]`);
        if (checkbox) {
          checkbox.checked = false;
          this.updateFilters();
        }
      });
    });
  }

  filterProducts(filters) {
    // This would make an AJAX request to get filtered products
    // For now, it's a placeholder
    console.log('Filtering products:', filters);
  }

  updateSort(sortBy) {
    // Update URL and reload products with new sort order
    const url = new URL(window.location);
    url.searchParams.set('sort_by', sortBy);
    window.location = url.toString();
  }

  clearAllFilters() {
    const checkboxes = document.querySelectorAll('.filter-option input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      checkbox.checked = false;
    });
    this.updateFilters();
  }

  // Color Swatches for Product Cards
  initColorSwatches() {
    const productCards = document.querySelectorAll('.product-card');

    productCards.forEach(card => {
      const swatches = card.querySelectorAll('.color-swatch');
      const primaryImage = card.querySelector('.product-card__image--primary');

      if (!swatches.length || !primaryImage) return;

      // Store original image
      const originalImageUrl = primaryImage.src;

      swatches.forEach(swatch => {
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Update active state
          swatches.forEach(s => s.classList.remove('color-swatch--active'));
          swatch.classList.add('color-swatch--active');

          // Change image if available
          const imageUrl = swatch.dataset.imageUrl;
          if (imageUrl && primaryImage) {
            primaryImage.src = imageUrl;
            primaryImage.srcset = `${imageUrl} 600w`;
          }
        });

        // Preview on hover (optional)
        swatch.addEventListener('mouseenter', () => {
          const imageUrl = swatch.dataset.imageUrl;
          if (imageUrl && primaryImage) {
            primaryImage.src = imageUrl;
            primaryImage.srcset = `${imageUrl} 600w`;
          }
        });

        swatch.addEventListener('mouseleave', () => {
          // Restore to active swatch image
          const activeSwatch = card.querySelector('.color-swatch--active');
          if (activeSwatch && activeSwatch.dataset.imageUrl && primaryImage) {
            primaryImage.src = activeSwatch.dataset.imageUrl;
            primaryImage.srcset = `${activeSwatch.dataset.imageUrl} 600w`;
          }
        });
      });
    });
  }

  // Language Toggle
  initLanguageToggle() {
    const languageToggle = document.getElementById('languageToggle');
    const languageOptions = document.querySelectorAll('.language-toggle__option');

    if (!languageToggle || !languageOptions.length) return;

    // Toggle dropdown on button click
    languageToggle.addEventListener('click', (e) => {
      e.stopPropagation();
      const dropdown = languageToggle.nextElementSibling;
      dropdown.classList.toggle('language-toggle__dropdown--active');
    });

    // Close dropdown on outside click
    document.addEventListener('click', () => {
      const dropdown = document.querySelector('.language-toggle__dropdown');
      if (dropdown) {
        dropdown.classList.remove('language-toggle__dropdown--active');
      }
    });

    // Handle language option clicks - let the links navigate to proper locale URLs
    languageOptions.forEach(option => {
      option.addEventListener('click', (e) => {
        // Don't prevent default - let the link navigation happen
        const newLang = option.dataset.lang;

        // Store preference for future reference
        localStorage.setItem('raffinato_language', newLang);
      });
    });
  }

  applyLanguage(lang) {
    // This is now handled by Shopify's locale system
    // The HTML dir and lang attributes are set in theme.liquid
    // This method is kept for backward compatibility
  }
}

// Initialize theme
document.addEventListener('DOMContentLoaded', () => {
  new RaffinatoTheme();
});

// Shopify theme editor support
document.addEventListener('shopify:section:load', () => {
  new RaffinatoTheme();
});